import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Ban, Trash2, Search, Mail, Calendar, User, AlertTriangle, ShieldBan } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';

export default function BlockedSenders() {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchSenders = useCallback(async () => {
    try {
      const res = await api.get('/blocked-senders');
      setSenders(res.data);
    } catch {
      toast.error('Failed to load blocked senders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSenders(); }, [fetchSenders]);

  const handleUnblock = async (sender) => {
    if (!window.confirm(`Unblock "${sender.email}"? Future emails from this sender will create tickets again.`)) return;
    try {
      await api.delete(`/blocked-senders/${sender.id}`);
      toast.success(`${sender.email} unblocked`);
      fetchSenders();
    } catch {
      toast.error('Failed to unblock sender');
    }
  };

  const filtered = senders.filter(s =>
    s.email.toLowerCase().includes(search.toLowerCase()) ||
    (s.notes || '').toLowerCase().includes(search.toLowerCase())
  );

  const getReasonBadge = (reason) => {
    switch (reason) {
      case 'spam': return <Badge variant="destructive" className="text-xs">Spam</Badge>;
      case 'unsolicited': return <Badge className="bg-orange-100 text-orange-700 text-xs">Unsolicited</Badge>;
      default: return <Badge className="bg-red-100 text-red-700 text-xs">Blocked</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="blocked-senders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="page-title">
            <Ban className="w-6 h-6 text-red-600" />
            Blocked Senders
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage blocked email addresses. Emails from blocked senders will not create tickets.</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search by email or notes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-10"
          data-testid="search-blocked"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-500">Total Blocked</div>
          <div className="text-2xl font-bold text-gray-900" data-testid="total-blocked">{senders.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-500">Spam</div>
          <div className="text-2xl font-bold text-red-600">{senders.filter(s => s.reason === 'spam').length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-500">Unsolicited</div>
          <div className="text-2xl font-bold text-orange-600">{senders.filter(s => s.reason === 'unsolicited').length}</div>
        </div>
      </div>

      {/* Senders List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <ShieldBan className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-600">
            {senders.length === 0 ? 'No blocked senders' : 'No results'}
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            {senders.length === 0 ? 'Block senders from the Tickets page to prevent unwanted emails from creating tickets.' : 'Try a different search term.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {filtered.map(sender => (
              <div key={sender.id} className="flex items-center justify-between px-4 sm:px-6 py-4 hover:bg-gray-50 transition-colors" data-testid={`blocked-sender-${sender.id}`}>
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 truncate">{sender.email}</span>
                      {getReasonBadge(sender.reason)}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {sender.blocked_at ? format(new Date(sender.blocked_at), 'MMM d, yyyy h:mm a') : 'Unknown'}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {sender.blocked_by || 'System'}
                      </span>
                    </div>
                    {sender.notes && (
                      <p className="text-xs text-gray-400 mt-1 truncate">{sender.notes}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUnblock(sender)}
                  className="text-red-600 hover:bg-red-50 border-red-200 flex-shrink-0 ml-3"
                  data-testid={`unblock-btn-${sender.id}`}
                >
                  <Trash2 className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Unblock</span>
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
